import * as raf from 'raf'
import * as React from 'react'

import get from 'lodash-es/get'

import { reportAudioPlayClick } from '../../../../common/analytics/resultAnalytics'
import PauseIcon from '../../icons/pause'
import PlayIcon from '../../icons/play'
import SpinnerIcon from '../../icons/spinner'

import * as styles from './audioPlayerSmall.module.css'

interface IProps {
  src: string
  onPlay: () => any
  isActive: boolean
}

interface IState {
  playing: boolean
  loading: boolean
  playBarProgress: number
  playPromise: any
}

export default class AudioPlayerSmall extends React.Component<IProps, IState> {
  private audioElement: React.RefObject<HTMLAudioElement>

  constructor(props: IProps) {
    super(props)

    this.state = {
      playing: false,
      loading: false,
      playBarProgress: 0,
      playPromise: null
    }

    this.togglePlay = this.togglePlay.bind(this)
    this.onPlay = this.onPlay.bind(this)
    this.onPause = this.onPause.bind(this)
    this.audioCanPlay = this.audioCanPlay.bind(this)
    this.onAudioProgress = this.onAudioProgress.bind(this)
    this.audioElement = React.createRef()
  }

  public componentDidUpdate(prevProps: IProps) {
    if (this.audioElement) {
      if (prevProps.isActive && !this.props.isActive) {
        this.tryPause()
      }
    }
  }

  public render() {
    const { src } = this.props
    const { playing, loading, playBarProgress } = this.state

    let buttonIcon = null
    if (loading) {
      buttonIcon = <SpinnerIcon className={styles.spinningIcon} />
    } else {
      if (playing) {
        buttonIcon = <PauseIcon className={styles.whiteIcon} />
      } else {
        buttonIcon = <PlayIcon className={styles.icon} />
      }
    }

    return (
      <div className={styles.container}>
        <audio
          ref={this.audioElement}
          preload="none"
          onPlay={this.onPlay}
          onPause={this.onPause}
          onCanPlay={this.audioCanPlay}
        >
          <source src={src} type="audio/mpeg" />
        </audio>
        <button onClick={this.togglePlay} className={styles.playButton}>
          {buttonIcon}
        </button>
        <div className={styles.progressBarContainer}>
          <div
            className={styles.progressBar}
            style={{
              width: `${playBarProgress * 100}%`
            }}
          />
        </div>
      </div>
    )
  }

  private onPause() {
    this.setState({ playing: false })
  }

  private onPlay() {
    reportAudioPlayClick(get(this.props, 'item.id'))
    this.props.onPlay()
    this.setState({ playing: true })
    raf(this.onAudioProgress)
  }

  private tryPause() {
    if (this.state.playPromise) {
      this.state.playPromise.then(() => {
        if (this.audioElement.current) {
          this.audioElement.current.pause()
        }
      })
    }
  }

  private tryPlay() {
    if (this.audioElement.current) {
      /*
       For some old browsers (Safari 9) play() does not return promise but undefined.
       Let's handle that case by returning resolved promise instead.
       */
      return (this.audioElement.current.play() || Promise.resolve()).catch(
        error => {
          // Throw error only if HTMLMediaElement still exists, if it's unmounted let's ignored it.
          // HTMLMediaElement internally call pause when removed from DOM. If media is still loading when unmounted
          // it caused an error "play() interrupted by pause()". There is no way to wait for media to play in the process of
          // unmounting
          if (this.audioElement) {
            throw error
          }
        }
      )
    }

    return null
  }

  private togglePlay() {
    const { playing } = this.state
    const audioElement = this.audioElement.current

    if (audioElement) {
      const isTrulyPlaying =
        audioElement.currentTime > 0 &&
        !audioElement.paused &&
        !audioElement.ended &&
        audioElement.readyState > 2
      if (playing && isTrulyPlaying) {
        this.tryPause()
      } else if (!isTrulyPlaying) {
        this.setState({
          playPromise: this.tryPlay()
        })
        this.showLoading()
      }
    }
  }

  private onAudioProgress() {
    if (this.state.playing) {
      if (this.audioElement.current) {
        this.setState({
          playBarProgress:
            this.audioElement.current.currentTime /
            this.audioElement.current.duration
        })
      }
      raf(this.onAudioProgress)
    }
  }

  private showLoading() {
    const audioElement = this.audioElement.current

    if (audioElement && (audioElement.seeking || audioElement.readyState < 3)) {
      this.setState({ loading: true })
    } else {
      this.setState({ loading: false })
    }
  }

  private audioCanPlay() {
    this.showLoading()
  }
}
